/**
 * 装配体标准配合控制器。
 *
 * 文件职责：
 * 1. 订阅 SelectionUpdated 并转换为 Mate 候选；
 * 2. 调用 compatibility / preview / document / command 领域模块；
 * 3. 将预览 transform 应用到当前场景，并在取消时恢复。
 */

import type { Subscription } from 'rxjs';
import type { Object3D } from 'three/webgpu';
import type { Viewer } from '../../viewer/Viewer';
import {
  EntityInteractionEventType,
  type EntitySelectedEventPayload,
  type SelectionUpdatedEventPayload,
} from '../../manager';
import type { ComponentInstanceSnapshot } from '../component-instance';
import {
  createAssemblyMateDocument,
  fromAssemblyMateDocumentJSON,
  replaceAssemblyMateDocumentByAssemblyScope,
  restoreAssemblyMateDocument,
  restoreAssemblyMateDocumentReferences,
  serializeAssemblyMateDocument,
  setAssemblyMateDocumentComponents,
  type AssemblyMateDocumentAssemblyScope,
  type AssemblyMateDocumentModel,
  type AssemblyMateDocumentJSON,
  type LockAssemblyConcentricRotationMatesScope,
  toAssemblyMateDocumentJSON,
  toAssemblyMateDocumentJSONByAssembly,
} from '../document';
import { executeAssemblyMateCommand } from '../command';
import {
  AssemblyMateType,
  MateAlignment,
  MateConcentricMisalignmentType,
  type MateDistanceEntityModes,
  MateDistanceMode,
  MateSolveStatus,
  type AssemblyMateDefinition,
} from '../definition';
import { resolveAssemblyMateCompatibility } from '../compatibility';
import {
  absorbAssemblyMateSelectionCandidates,
  createAssemblyMateSelectionCandidatesFromEntityRefs,
  createAssemblyMateSelectionCandidatesFromSnapshot,
  removeAssemblyMateSelectionCandidateAt,
  removeAssemblyMateSelectionCandidatesAt,
  replaceAssemblyMateSelectionCandidateAt,
  resolveMateCandidatePair,
  type AssemblyMateSelectionCandidate,
} from '../selection';
import { createAssemblyMatePreview, type AssemblyMatePreviewResult } from '../preview';
import { captureStandardMateSolverUnsolvedResult, type StandardMateSolverInput } from '../solver';
import type {
  AssemblyMateCommitResult,
  AssemblyMateComponentRotationAxis,
  AssemblyMateCommittedSolveResult,
  AssemblyMateDocumentChangeContext,
  AssemblyMateControllerOptions,
  AssemblyMateControllerState,
  AssemblyMateControllerViewModel,
  AssemblyMateAlignmentBranchAvailability,
  AssemblyMateSelectionTarget,
  AssemblyMateStateListener,
} from './AssemblyMateControllerTypes';
import { replaceAssemblyMateCandidateEntityRef } from './support/AssemblyMateControllerStateHelpers';
import {
  canUseAssemblyMateAngleReferenceEntity,
  isAssemblyMateAutoAngleReferenceEntity,
} from '../definition/AssemblyMateAngleReference';
import {
  AssemblyMateControllerRuntime,
  type AssemblyMatePreviewRestoreOptions,
} from './AssemblyMateControllerRuntime';
import {
  canSubmitAssemblyMateSolveStatus,
  createDefaultAssemblyMateControllerState,
  createEmptyAssemblyMateControllerSessionStatePatch,
  createPostSubmitAssemblyMateControllerStatePatch,
  normalizeAssemblyMatePropertyManagerControllerState,
  resolveAssemblyMateAlignmentEnabled,
  resolveAssemblyMateLockRotationEnabled,
  resolveAssemblyMatePropertyManagerPreviewState,
  resolveDefaultAssemblyMateType,
} from './policy/AssemblyMateControllerStatePolicy';
import { resolveAssemblyMateConcentricMisalignmentStatePatch } from './policy/AssemblyMateConcentricMisalignmentStatePolicy';
import { AssemblyMateControllerSolveService } from './AssemblyMateControllerSolveService';
import { AssemblyMateDefinitionDraftService } from './AssemblyMateDefinitionDraftService';
import { resolveAssemblyMateComponentInstanceIds } from '../utils';

/** 默认装配体 ID。 */
const DEFAULT_ASSEMBLY_ID = 'default-assembly';

/** Viewer 公开的选择模式。 */
type AssemblyMateViewerSelectionMode = 'object' | 'face' | 'edge' | 'vertex' | 'face-edge' | 'topology';

/** PropertyManager 预览状态解析结果。 */
type AssemblyMatePropertyManagerPreviewState = ReturnType<typeof resolveAssemblyMatePropertyManagerPreviewState>;

/** 配合命令需要同时吸收点、线/边和面，因此进入完整拓扑选择模式。 */
const ASSEMBLY_MATE_SELECTION_MODE: AssemblyMateViewerSelectionMode = 'topology';

/** 两个对齐分支均可选的默认状态。 */
const ENABLED_ALIGNMENT_BRANCH_AVAILABILITY: AssemblyMateAlignmentBranchAvailability = {
  aligned: true,
  antiAligned: true,
};

/** 两个对齐分支均不可选的状态。 */
const DISABLED_ALIGNMENT_BRANCH_AVAILABILITY: AssemblyMateAlignmentBranchAvailability = {
  aligned: false,
  antiAligned: false,
};

/**
 * 当前草稿 Mate 的一次预览求解评估结果。
 */
interface AssemblyMatePreviewEvaluation {
  /** 独立求解器输出的预览结果。 */
  readonly result: AssemblyMatePreviewResult;
  /** 面向 PropertyManager 的提交可用性状态。 */
  readonly previewState: AssemblyMatePropertyManagerPreviewState;
}

/**
 * 当前草稿 Mate 的预览求解评估选项。
 */
interface AssemblyMatePreviewEvaluationOptions {
  /**
   * 是否允许临时 Mate 使用初始姿态增强。
   *
   * 设计说明：
   * 初次预览要帮助用户把第二选择零件贴到合理位置；提交阶段若已经有预览快照，
   * 则当前场景位姿就是用户认可的结果，不能再次执行初始贴边。
   */
  readonly useTransientInitialPlacement?: boolean;
}

/**
 * 草稿 Mate 预览刷新选项。
 */
interface AssemblyMateRefreshPreviewOptions extends AssemblyMatePreviewEvaluationOptions {
  /**
   * 是否保留当前已经应用到场景中的预览位姿作为下一次求解基线。
   *
   * 设计说明：
   * 切换同向/反向对齐时，用户期望当前已拖放或已预览的位置继续作为翻转基准；
   * 不能先回滚到首次预览前的位置再求解，否则会把零部件拉回旧位置。
   */
  readonly preserveCurrentPreviewPlacement?: boolean;
}

/**
 * 当前草稿 Mate 的对齐分支解析结果。
 */
interface AssemblyMateAlignmentBranchResolution {
  /** 每个对齐分支是否可用于当前求解上下文。 */
  readonly availability: AssemblyMateAlignmentBranchAvailability;
  /** 应写回 PropertyManager 的对齐分支。 */
  readonly alignment: MateAlignment;
}

/**
 * 已完成对齐分支解析的预览输入。
 */
interface AssemblyMateResolvedPreviewInput {
  /** 按可解对齐分支修正后的 Mate Definition。 */
  readonly definition: AssemblyMateDefinition;
  /** 与修正后 Definition 保持一致的 solver input。 */
  readonly solverInput: StandardMateSolverInput;
}

/**
 * Mate 文档组件状态快照选项。
 */
interface AssemblyMateDocumentComponentStateSnapshotOptions {
  /**
   * 是否优先读取预览应用前的组件快照。
   *
   * 设计说明：
   * 提交 Mate 时，before 文档应代表进入预览前的真实位姿；但场景对象不能为了构造事务而发生一次实际回滚。
   */
  readonly preferPreviewRestoreStates?: boolean;
}

/**
 * Mate 文档载入选项。
 */
export interface AssemblyMateLoadDocumentOptions {
  /** 是否在载入时通过 AssemblyTopologySelectionRef 刷新 MateEntityRef 几何快照。 */
  readonly restoreReferences?: boolean;
  /** 是否触发文档变更通知。 */
  readonly notifyDocumentChanged?: boolean;
  /**
   * 是否保留当前场景中的组件位姿，并以真实场景状态刷新载入文档的组件快照。
   *
   * 设计说明：
   * 该选项只用于局部模型替换事务恢复清理后的根 Mate 文档。局部替换已经提交了新场景，
   * 根文档此时只负责恢复范围外 Mate 语义，不能把切换前保存的全量组件位姿重新写回场景。
   * 完整文档首次加载不得启用该选项，仍应按文档快照恢复所有组件位姿。
   */
  readonly preserveCurrentComponentStates?: boolean;
}

/**
 * 按装配体节点导出 Mate JSON 的选项。
 */
export interface AssemblyMateToJSONByAssemblyOptions {
  /** 导出范围；默认只导出直接归属于目标装配体的 Mate。 */
  readonly scope?: AssemblyMateDocumentAssemblyScope;
}

/**
 * 按装配体节点恢复 Mate JSON 的选项。
 */
export interface AssemblyMateFromJSONByAssemblyOptions extends AssemblyMateLoadDocumentOptions {
  /** 替换范围；默认只替换直接归属于目标装配体的 Mate。 */
  readonly scope?: AssemblyMateDocumentAssemblyScope;
}

/**
 * 装配体标准配合控制器。
 */
export class AssemblyMateController {
  private readonly viewer: Viewer;
  private readonly options: AssemblyMateControllerOptions;
  private state: AssemblyMateControllerState = createDefaultAssemblyMateControllerState();
  private readonly stateListeners = new Set<AssemblyMateStateListener>();
  private selectionSubscription: Subscription | null = null;
  private mateDocument: AssemblyMateDocumentModel;
  private sessionMateIds: string[] = [];
  private previewResult: AssemblyMatePreviewResult | null = null;
  private readonly runtime: AssemblyMateControllerRuntime;
  private readonly solveService: AssemblyMateControllerSolveService;
  private readonly draftService: AssemblyMateDefinitionDraftService;
  private previousSelectionMode: AssemblyMateViewerSelectionMode | null = null;
  private preferredRoundDistanceMode: MateDistanceMode | null = null;
  private preferredRoundDistanceEntityModes: MateDistanceEntityModes | null = null;

  /**
   * 构造函数。
   *
   * @param viewer Viewer 实例。
   * @param options 控制器选项。
   */
  public constructor(viewer: Viewer, options: AssemblyMateControllerOptions = {}) {
    this.viewer = viewer;
    this.options = options;
    this.runtime = new AssemblyMateControllerRuntime(viewer, () => this.state);
    this.draftService = new AssemblyMateDefinitionDraftService(this.runtime);
    this.solveService = new AssemblyMateControllerSolveService({
      viewer,
      runtime: this.runtime,
      resolveMateDocument: () => this.mateDocument,
      commitMateDocument: (document, context) => {
        this.mateDocument = document;
        this.notifyMateDocumentChangedInBackground(context);
      },
    });
    this.mateDocument = createAssemblyMateDocument({
      assemblyId: options.assemblyId ?? DEFAULT_ASSEMBLY_ID,
    });
  }

  /**
   * 获取当前视图模型。
   *
   * @returns 视图模型。
   */
  public getViewModel(): AssemblyMateControllerViewModel {
    return {
      state: { ...this.state },
      document: this.mateDocument,
      sessionMates: this.resolveSessionMates(),
    };
  }

  /**
   * 订阅控制器视图模型变化。
   *
   * @param listener 状态监听器。
   * @returns 取消订阅函数。
   */
  public subscribeStateChanged(listener: AssemblyMateStateListener): () => void {
    this.stateListeners.add(listener);
    listener(this.getViewModel());
    return () => {
      this.stateListeners.delete(listener);
    };
  }

  /**
   * 获取 Mate 文档。
   *
   * @returns Mate 文档。
   */
  public getDocument(): AssemblyMateDocumentModel {
    return this.mateDocument;
  }

  /**
   * 导出当前 Mate 文档 JSON。
   *
   * 设计说明：
   * 1. 该 API 是装配体保存链路读取标准配合关系的唯一入口；
   * 2. 返回值只包含可序列化数据，不包含 Viewer、Object3D 或 Three.js 资源；
   * 3. Mate 命令激活时不读取场景预览 transform，避免把尚未确认的预览姿态写入项目。
   *
   * @returns 可持久化的 Mate 文档 JSON。
   */
  public toJSON(): AssemblyMateDocumentJSON {
    const document = this.state.active
      ? this.mateDocument
      : this.createDocumentWithCurrentComponentStates(this.mateDocument);
    return toAssemblyMateDocumentJSON(document);
  }

  /**
   * 按装配体节点导出局部 Mate 文档 JSON。
   *
   * 设计说明：
   * 1. 该 API 用于单独保存顶层装配体或某个子装配体下的 Mate 数据；
   * 2. 过滤规则仍由 cad-core 根据 Mate `assemblyId` 处理，调用方不需要也不应该自行过滤；
   * 3. Mate 命令激活时不读取预览 transform，避免把未确认的预览姿态写入局部文档。
   *
   * @param assemblyNodeId 目标装配体节点 ID。
   * @param options 导出选项。
   * @returns 指定装配体范围内的 Mate 文档 JSON。
   */
  public toJSONByAssembly(
    assemblyNodeId: string,
    options: AssemblyMateToJSONByAssemblyOptions = {},
  ): AssemblyMateDocumentJSON {
    const document = this.state.active
      ? this.mateDocument
      : this.createDocumentWithCurrentComponentStates(this.mateDocument);
    return toAssemblyMateDocumentJSONByAssembly(document, {
      assemblyNodeId,
      scope: options.scope,
    });
  }

  /**
   * 从 Mate JSON 恢复当前控制器文档。
   *
   * @param data 项目文件中的 Mate JSON。
   * @param options 装载选项。
   */
  public fromJSON(
    data: unknown,
    options: AssemblyMateLoadDocumentOptions = {},
  ): void {
    this.loadDocument(fromAssemblyMateDocumentJSON(data), options);
  }

  /**
   * 按装配体节点恢复局部 Mate 文档 JSON。
   *
   * 设计说明：
   * 1. 该 API 不会清空整个工程 Mate，只替换目标装配体范围内的 Mate；
   * 2. 范围外 Mate 保持不变，避免保存/加载子装配体时破坏其它装配体约束；
   * 3. 传入 JSON 的 `assemblyId` 必须等于目标装配体节点 ID，防止跨装配体误写。
   *
   * @param assemblyNodeId 目标装配体节点 ID。
   * @param data 局部 Mate JSON。
   * @param options 恢复选项。
   */
  public fromJSONByAssembly(
    assemblyNodeId: string,
    data: unknown,
    options: AssemblyMateFromJSONByAssemblyOptions = {},
  ): void {
    const { scope, ...loadOptions } = options;
    const currentDocument = this.state.active
      ? this.mateDocument
      : this.createDocumentWithCurrentComponentStates(this.mateDocument);
    const nextDocument = replaceAssemblyMateDocumentByAssemblyScope({
      targetDocument: currentDocument,
      sourceDocument: fromAssemblyMateDocumentJSON(data),
      assemblyNodeId,
      scope,
    });
    this.loadDocument(nextDocument, loadOptions);
  }

  /**
   * 从持久化数据装载 Mate 文档。
   *
   * 设计说明：
   * 1. 控制器只接收 cad-core 的纯文档模型，宿主负责从文件、IndexedDB 或后端读取数据；
   * 2. 装载文档会清理当前命令态候选和预览，避免旧会话高亮或 transform 预览污染恢复后的装配约束；
   * 3. 默认不触发持久化回调，防止“读取后立刻写回”形成宿主层无意义的保存循环。
   *
   * @param document 需要装载的 Mate 文档。
   * @param options 装载选项。
   */
  public loadDocument(
    document: AssemblyMateDocumentModel,
    options: AssemblyMateLoadDocumentOptions = {},
  ): void {
    this.restorePreview();
    this.sessionMateIds = [];
    this.runtime.clearCandidateHighlightPayloads();
    this.runtime.invalidateComponentTransformRootIndex();
    const serializedDocument = serializeAssemblyMateDocument(document);
    const restoredDocument = restoreAssemblyMateDocument(serializedDocument);
    const documentWithResolvedComponentStates = options.preserveCurrentComponentStates
      ? this.createDocumentWithCurrentComponentStates(restoredDocument)
      : restoredDocument;

    /**
     * 持久化 Mate 的几何描述符是选择瞬间的 world-space 快照。
     * 重新加载项目时必须先把组件恢复到文档保存的 transform，再解析拓扑引用；
     * 否则 `componentWorldTransformAtSelection` 会记录加载初始姿态，后续动态求解时
     * world delta 会以错误基线变换圆柱轴线，典型表现就是 Concentric 拖动后不同轴。
     */
    this.mateDocument = documentWithResolvedComponentStates;
    this.runtime.applyDocumentComponentStates(documentWithResolvedComponentStates.components);

    this.mateDocument = options.restoreReferences
      ? restoreAssemblyMateDocumentReferences({
          document: documentWithResolvedComponentStates,
          resolveEntity: (entity) => this.runtime.resolveMateEntityReference(entity),
        })
      : documentWithResolvedComponentStates;
    this.runtime.applyDocumentComponentStates(this.mateDocument.components);
    this.patchState(createDefaultAssemblyMateControllerState());
    if (options.notifyDocumentChanged) {
      this.notifyMateDocumentChangedInBackground({
        source: 'load',
        recordHistory: false,
      });
    }
  }

  /**
   * 按组件快照解析场景对象。
   *
   * 设计说明：
   * MoveComponent 取消预览或提交失败回滚时需要把 Mate 联动组件恢复到 before 快照，
   * 但不应该复制一份装配节点查找逻辑；因此由 Mate 控制器暴露稳定查询入口。
   *
   * @param snapshot 组件实例快照。
   * @returns 场景对象；缺失时返回 `null`。
   */
  public resolveSceneObjectForComponentSnapshot(snapshot: ComponentInstanceSnapshot): Object3D | null {
    return this.runtime.resolveSceneObjectForComponentSnapshot(snapshot);
  }

  /**
   * 判断控制器是否激活。
   *
   * @returns 是否激活。
   */
  public isActive(): boolean {
    return this.state.active;
  }

  /**
   * 打开 Mate PropertyManager 会话。
   */
  public open(): void {
    if (!this.selectionSubscription) {
      const stream = this.viewer.observeEntityInteraction(EntityInteractionEventType.SelectionUpdated);
      this.selectionSubscription = stream?.subscribe((payload: SelectionUpdatedEventPayload) => {
        this.handleSelectionUpdated(payload);
      }) ?? null;
    }

    this.enterMateSelectionMode();
    this.restorePreview();
    this.sessionMateIds = [];
    this.runtime.clearCandidateHighlightPayloads();
    this.patchState({
      active: true,
      ...createEmptyAssemblyMateControllerSessionStatePatch(),
    });
    this.activateMateSessionMoveCapability();
  }

  /**
   * 设置配合类型。
   *
   * @param mateType 配合类型。
   */
  public setMateType(mateType: AssemblyMateType): void {
    const preserveCurrentDistance = this.state.selectedMateType === AssemblyMateType.Distance
      && mateType === AssemblyMateType.Distance;
    const lockRotationEnabled = resolveAssemblyMateLockRotationEnabled(
      mateType,
      this.state.candidates,
      { mateDefinitions: this.mateDocument.mates },
    );
    const alignmentEnabled = resolveAssemblyMateAlignmentEnabled(mateType, this.state.candidates);
    const nextState = {
      selectedMateType: mateType,
      selectionTarget: mateType === AssemblyMateType.Angle ? this.state.selectionTarget : 'mateEntities',
      angleReferenceEntity: mateType === AssemblyMateType.Angle ? this.state.angleReferenceEntity : null,
      candidateReplacementIndex: null,
      alignmentEnabled,
      alignmentBranchAvailability: alignmentEnabled
        ? ENABLED_ALIGNMENT_BRANCH_AVAILABILITY
        : DISABLED_ALIGNMENT_BRANCH_AVAILABILITY,
      lockRotationEnabled,
      lockRotation: lockRotationEnabled ? this.state.lockRotation : false,
    } satisfies Partial<AssemblyMateControllerState>;
    const provisionalState = {
      ...this.state,
      ...nextState,
    } as AssemblyMateControllerState;
    this.patchState({
      ...nextState,
      ...resolveAssemblyMateConcentricMisalignmentStatePatch({
        mateType,
        candidates: this.state.candidates,
        mateDefinitions: this.mateDocument.mates,
        state: provisionalState,
      }),
      ...this.draftService.resolveMeasuredDistancePatch(
        mateType,
        this.state.candidates,
        this.state,
        {
          preserveCurrentDistance,
          preferredDistanceMode: this.preferredRoundDistanceMode,
          preferredDistanceEntityModes: this.preferredRoundDistanceEntityModes,
        },
      ),
    });
    this.refreshPreview();
  }

  /**
   * 设置当前图形区选择吸收目标。
   *
   * 设计说明：
   * SOLIDWORKS 的 Angle Mate Reference Entity 不占用 `Entities to Mate` 的两个槽位。
   * 因此控制器需要知道当前激活的是普通配合选择还是角度参考实体输入框，web-client 只转发输入框激活状态。
   *
   * @param selectionTarget 选择吸收目标。
   */
  public setSelectionTarget(selectionTarget: AssemblyMateSelectionTarget): void {
    if (selectionTarget === 'angleReference' && this.state.selectedMateType !== AssemblyMateType.Angle) {
      return;
    }
    this.patchState({
      selectionTarget,
      candidateReplacementIndex: selectionTarget === 'mateEntities' ? this.state.candidateReplacementIndex : null,
    });
  }

  /**
   * 设置下一次普通 Mate 实体选择需要替换的槽位。
   *
   * 设计说明：
   * SOLIDWORKS 允许用户点中 `Entities to Mate` 的某一行后重新从图形区选取实体。
   * UI 只传入行号，候选替换、去重、兼容性和预览刷新全部保留在 cad-core 控制器内。
   *
   * @param index 要替换的槽位索引；传入 null 或非法值时恢复顺序吸收。
   */
  public setCandidateReplacementIndex(index: number | null): void {
    const candidateReplacementIndex = index !== null && Number.isInteger(index) && index >= 0 && index < 2
      ? index
      : null;
    this.patchState({ candidateReplacementIndex });
  }

  /**
   * 清除 Angle Mate 手动参考实体，回到自动参考轴。
   */
  public clearAngleReferenceEntity(): void {
    this.patchState({
      angleReferenceEntity: null,
      selectionTarget: 'mateEntities',
    });
    this.runtime.syncCandidateHighlights(this.state.candidates);
    this.refreshPreview();
  }

  /**
   * 设置对齐方向。
   *
   * @param alignment 对齐方向。
   */
  public setAlignment(alignment: MateAlignment): void {
    if (!this.state.alignmentEnabled || !this.isAlignmentBranchAvailable(alignment)) {
      return;
    }
    const shouldPreserveCurrentPreviewPlacement = this.state.showPreview && this.runtime.hasPreviewSnapshot();
    this.patchState({ alignment });
    this.refreshPreview(false, {
      preserveCurrentPreviewPlacement: shouldPreserveCurrentPreviewPlacement,
      useTransientInitialPlacement: shouldPreserveCurrentPreviewPlacement ? false : undefined,
    });
  }

  /**
   * 设置距离。
   *
   * @param distance 距离值。
   */
  public setDistance(distance: number): void {
    this.patchState({ distance });
    this.refreshPreview();
  }

  /**
   * 设置距离配合测量条件。
   *
   * @param distanceMode 距离配合测量条件。
   */
  public setDistanceMode(distanceMode: MateDistanceMode): void {
    if (this.state.selectedMateType !== AssemblyMateType.Distance) {
      return;
    }

    const enabledOption = this.state.distanceModeOptions.find((item) => (
      item.mode === distanceMode && item.enabled
    ));
    if (!enabledOption) {
      return;
    }

    this.preferredRoundDistanceMode = distanceMode;
    const distanceEntityModes = {
      firstEntityMode: this.state.firstDistanceModeOptions.length > 0
        ? distanceMode
        : this.state.distanceEntityModes.firstEntityMode,
      secondEntityMode: this.state.secondDistanceModeOptions.length > 0
        ? distanceMode
        : this.state.distanceEntityModes.secondEntityMode,
    };
    this.preferredRoundDistanceEntityModes = distanceEntityModes;
    this.patchState({ distanceMode, distanceEntityModes });
    this.refreshPreview();
  }

  /**
   * 设置指定选择实体的圆形距离条件。
   *
   * @param entityIndex 选择实体索引，0 表示第一选择，1 表示第二选择。
   * @param distanceMode 距离测量条件。
   */
  public setDistanceEntityMode(entityIndex: 0 | 1, distanceMode: MateDistanceMode): void {
    if (this.state.selectedMateType !== AssemblyMateType.Distance) {
      return;
    }

    const modeOptions = entityIndex === 0
      ? this.state.firstDistanceModeOptions
      : this.state.secondDistanceModeOptions;
    const enabledOption = modeOptions.find((item) => (
      item.mode === distanceMode && item.enabled
    ));
    if (!enabledOption) {
      return;
    }

    const distanceEntityModes = entityIndex === 0
      ? {
          ...this.state.distanceEntityModes,
          firstEntityMode: distanceMode,
        }
      : {
          ...this.state.distanceEntityModes,
          secondEntityMode: distanceMode,
        };
    this.preferredRoundDistanceEntityModes = distanceEntityModes;
    this.patchState({
      distanceEntityModes,
      distanceMode: distanceEntityModes.firstEntityMode === distanceEntityModes.secondEntityMode
        ? distanceEntityModes.firstEntityMode
        : this.state.distanceMode,
    });
    this.refreshPreview();
  }

  /**
   * 设置角度。
   *
   * @param angle 角度值，单位弧度。
   */
  public setAngle(angle: number): void {
    this.patchState({ angle });
    this.refreshPreview();
  }

  /**
   * 设置尺寸翻转。
   *
   * @param flipped 是否翻转。
   */
  public setDimensionFlipped(flipped: boolean): void {
    this.patchState({ dimensionFlipped: flipped });
    this.refreshPreview();
  }

  /**
   * 设置同轴心锁定旋转。
   *
   * @param lockRotation 是否锁定旋转。
   */
  public setLockRotation(lockRotation: boolean): void {
    if (!this.state.lockRotationEnabled) {
      return;
    }
    this.patchState({ lockRotation });
    this.refreshPreview();
  }

  /**
   * 设置同轴心错位配合是否启用。
   *
   * @param enabled 是否启用错位同轴心。
   */
  public setConcentricMisalignmentEnabled(enabled: boolean): void {
    const nextState = {
      ...this.state,
      concentricMisalignmentEnabled: enabled,
    } as AssemblyMateControllerState;
    this.patchState(resolveAssemblyMateConcentricMisalignmentStatePatch({
      mateType: this.state.selectedMateType,
      candidates: this.state.candidates,
      mateDefinitions: this.mateDocument.mates,
      state: nextState,
    }));
    this.refreshPreview();
  }

  /**
   * 设置同轴心错位配合的偏差分配方式。
   *
   * @param type 偏差分配方式。
   */
  public setConcentricMisalignmentType(type: MateConcentricMisalignmentType): void {
    if (!Object.values(MateConcentricMisalignmentType).includes(type)) {
      return;
    }
    this.patchState({ concentricMisalignmentType: type });
    this.refreshPreview();
  }

  /**
   * 设置同轴心错位配合链接的已提交 Mate。
   *
   * @param linkedMateId linked mate ID。
   */
  public setConcentricMisalignmentLinkedMateId(linkedMateId: string): void {
    const linkedMate = this.state.concentricMisalignmentLinkedMateOptions.find((item) => (
      item.mateId === linkedMateId
    ));
    if (!linkedMate) {
      return;
    }
    this.patchState({ concentricMisalignmentLinkedMateId: linkedMateId });
    this.refreshPreview();
  }

  /**
   * 设置同轴心错位配合允许的最大偏差。
   *
   * @param maximumDeviation 最大偏差。
   */
  public setConcentricMisalignmentMaximumDeviation(maximumDeviation: number): void {
    if (!Number.isFinite(maximumDeviation) || maximumDeviation < 0) {
      return;
    }
    this.patchState({ concentricMisalignmentMaximumDeviation: maximumDeviation });
    this.refreshPreview();
  }

  /**
   * 设置显示预览。
   *
   * @param showPreview 是否显示预览。
   */
  public setShowPreview(showPreview: boolean): void {
    this.patchState({ showPreview });
    this.refreshPreview();
  }

  /**
   * 设置是否使第一个选择所属零部件临时透明。
   *
   * @param firstSelectionTransparent 是否透明显示第一个选择所属零部件。
   */
  public setFirstSelectionTransparent(firstSelectionTransparent: boolean): void {
    this.patchState({ firstSelectionTransparent });
  }

  /**
   * 提交当前配合。
   *
   * @returns 提交结果；条件不足时返回 null。
   */
  public async submit(): Promise<AssemblyMateCommitResult | null> {
    const definition = this.draftService.createCurrentMateDefinition(this.state, this.mateDocument);
    if (!definition) {
      return null;
    }

    const commandComponentIds = resolveAssemblyMateComponentInstanceIds(definition);
    const beforeDocument = this.createDocumentWithCurrentComponentStates(this.mateDocument, commandComponentIds, {
      preferPreviewRestoreStates: true,
    });
    const shouldPreserveCurrentPreviewPlacement = this.state.showPreview && this.runtime.hasPreviewSnapshot();
    const previewEvaluation = this.resolvePreviewEvaluation(definition, {
      useTransientInitialPlacement: !shouldPreserveCurrentPreviewPlacement,
    });
    if (!previewEvaluation || !canSubmitAssemblyMateSolveStatus(previewEvaluation.previewState.solveStatus)) {
      return null;
    }
    if (previewEvaluation.previewState.solveStatus === MateSolveStatus.Solved) {
      this.runtime.applyPreviewTransforms(previewEvaluation.result, this.state.candidates);
    }

    const committedDefinition = {
      ...definition,
      solveStatus: previewEvaluation.previewState.solveStatus,
      errorMessage: previewEvaluation.previewState.diagnostic?.message,
    };
    const previousSessionMateIds = this.sessionMateIds;
    let nextSessionMateIds = previousSessionMateIds;
    let commandDocument: AssemblyMateDocumentModel;
    if (this.state.editingMateId) {
      commandDocument = executeAssemblyMateCommand(
        beforeDocument,
        {
          type: 'assemblyMate.edit',
          mateId: this.state.editingMateId,
          updater: () => committedDefinition,
        },
      ).document;
    } else {
      commandDocument = executeAssemblyMateCommand(
        beforeDocument,
        {
          type: 'assemblyMate.add',
          mateDefinition: committedDefinition,
        },
      ).document;
      nextSessionMateIds = [...this.sessionMateIds, committedDefinition.mateId];
    }
    const nextDocument = this.createDocumentWithCurrentComponentStates(commandDocument, [
      ...commandComponentIds,
      ...Object.keys(previewEvaluation.result.componentTransforms),
    ]);

    const result: AssemblyMateCommitResult = {
      definition: committedDefinition,
      document: nextDocument,
      preview: previewEvaluation.result,
    };
    await this.commitCommandDocumentChange({
      beforeDocument,
      nextDocument,
      previousSessionMateIds,
      nextSessionMateIds,
      afterApply: () => this.options.onMateCommitted?.(result),
    });
    this.clearPreviewSnapshotOnly();
    this.runtime.clearCandidateHighlightPayloads();
    const wasEditingMate = Boolean(this.state.editingMateId);
    this.patchState(createPostSubmitAssemblyMateControllerStatePatch(this.state));
    if (wasEditingMate) {
      this.restoreMateSelectionMode();
    }
    return result;
  }

  /**
   * 从已提交 Mate 进入 PropertyManager 编辑会话。
   *
   * 设计说明：
   * 1. SOLIDWORKS 允许从 FeatureManager 的 Mate 节点编辑已有配合；
   * 2. web-client 只能传入稳定 `mateId`，候选恢复、兼容性、尺寸、对齐、预览都在 cad-core 内完成；
   * 3. 编辑会话不加入本次新增 Mate 列表，确认时执行 EditAssemblyMateCommand，而不是新增一条 Mate。
   *
   * @param mateId 要编辑的 Mate ID。
   * @returns 是否成功进入编辑会话。
   */
  public beginEditMate(mateId: string): boolean {
    const mate = this.mateDocument.mates.find((item) => item.mateId === mateId);
    if (!mate) {
      return false;
    }
    if (!this.selectionSubscription) {
      const stream = this.viewer.observeEntityInteraction(EntityInteractionEventType.SelectionUpdated);
      this.selectionSubscription = stream?.subscribe((payload: SelectionUpdatedEventPayload) => {
        this.handleSelectionUpdated(payload);
      }) ?? null;
    }

    this.enterMateSelectionMode();
    this.restorePreview();
    this.sessionMateIds = [];
    this.runtime.clearCandidateHighlightPayloads();
    const candidates = createAssemblyMateSelectionCandidatesFromEntityRefs([
      mate.firstEntity,
      mate.secondEntity,
    ]);
    const pair = resolveMateCandidatePair(candidates);
    const compatibility = pair
      ? resolveAssemblyMateCompatibility(pair[0].entityRef, pair[1].entityRef)
      : null;
    const lockRotationEnabled = resolveAssemblyMateLockRotationEnabled(
      mate.mateType,
      candidates,
      { mateDefinitions: this.mateDocument.mates },
    );
    const angleReferenceEntity = mate.mateType === AssemblyMateType.Angle
      && mate.referenceEntity
      && !isAssemblyMateAutoAngleReferenceEntity(mate.referenceEntity)
      ? mate.referenceEntity
      : null;
    const alignmentEnabled = resolveAssemblyMateAlignmentEnabled(mate.mateType, candidates);

    this.runtime.cacheMateDefinitionHighlightPayloads(mate);
    const nextState = {
      active: true,
      editingMateId: mate.mateId,
      candidates,
      candidateReplacementIndex: null,
      selectionTarget: 'mateEntities',
      angleReferenceEntity,
      compatibility,
      selectedMateType: mate.mateType,
      alignment: mate.alignment,
      alignmentEnabled,
      alignmentBranchAvailability: alignmentEnabled
        ? ENABLED_ALIGNMENT_BRANCH_AVAILABILITY
        : DISABLED_ALIGNMENT_BRANCH_AVAILABILITY,
      distance: mate.dimension?.kind === AssemblyMateType.Distance
        ? mate.dimension.value
        : this.state.distance,
      angle: mate.dimension?.kind === AssemblyMateType.Angle
        ? mate.dimension.value
        : this.state.angle,
      dimensionFlipped: mate.dimension?.flipped ?? false,
      lockRotationEnabled,
      lockRotation: lockRotationEnabled ? Boolean(mate.lockRotation) : false,
      concentricMisalignmentEnabled: Boolean(mate.concentricMisalignment),
      concentricMisalignmentType: mate.concentricMisalignment?.type ?? this.state.concentricMisalignmentType,
      concentricMisalignmentLinkedMateId: mate.concentricMisalignment?.linkedMateId ?? null,
      concentricMisalignmentMaximumDeviation: mate.concentricMisalignment?.maximumDeviation
        ?? this.state.concentricMisalignmentMaximumDeviation,
      ...this.draftService.resolveExistingDistancePatch(mate, candidates),
      solveStatus: MateSolveStatus.NotRun,
      errorMessage: null,
      canSubmit: false,
    } satisfies Partial<AssemblyMateControllerState>;
    this.patchState({
      ...nextState,
      ...resolveAssemblyMateConcentricMisalignmentStatePatch({
        mateType: mate.mateType,
        candidates,
        mateDefinitions: this.mateDocument.mates,
        state: {
          ...this.state,
          ...nextState,
        } as AssemblyMateControllerState,
      }),
    });
    this.refreshPreview();
    this.activateMateSessionMoveCapability();
    return true;
  }

  /**
   * 删除已经创建的配合。
   *
   * @param mateId 要删除的 Mate ID。
   */
  public async deleteMate(mateId: string): Promise<void> {
    this.restorePreview();
    const beforeDocument = this.createDocumentWithCurrentComponentStates(this.mateDocument);
    const nextDocument = this.createDocumentWithCurrentComponentStates(executeAssemblyMateCommand(
      beforeDocument,
      {
        type: 'assemblyMate.delete',
        mateId,
      },
    ).document);
    await this.commitCommandDocumentChange({
      beforeDocument,
      nextDocument,
      previousSessionMateIds: this.sessionMateIds,
      nextSessionMateIds: this.sessionMateIds.filter((item) => item !== mateId),
    });
    this.patchState({
      canSubmit: this.draftService.createCurrentMateDefinition(this.state, this.mateDocument) !== null,
    });
  }

  /**
   * 批量锁定当前文档中所有可锁定的同轴心 Mate 旋转自由度。
   *
   * 设计说明：
   * 1. SOLIDWORKS 在设计树 `Mates` 文件夹右键提供 `Lock Concentric Rotation`；
   * 2. 具体可锁定性由 cad-core 领域命令判断，web-client 不能解析 BRep 或修改 Mate Definition；
   * 3. 无可修改 Mate 时保持文档不变且不记录历史，避免空操作污染 Undo/Redo。
   *
   * @param scope 设计树节点传入的操作作用域；为空时处理整个 Mate 文档。
   * @returns 实际被锁定的 Mate ID 列表。
   */
  public async lockConcentricRotationForAllMates(
    scope?: LockAssemblyConcentricRotationMatesScope,
  ): Promise<readonly string[]> {
    this.restorePreview();
    const beforeDocument = this.createDocumentWithCurrentComponentStates(this.mateDocument);
    const result = executeAssemblyMateCommand(
      beforeDocument,
      { type: 'assemblyMate.lockConcentricRotation', scope },
    );
    if (result.affectedMateIds.length === 0) {
      return [];
    }

    await this.commitCommandDocumentChange({
      beforeDocument,
      nextDocument: this.createDocumentWithCurrentComponentStates(result.document),
      previousSessionMateIds: this.sessionMateIds,
      nextSessionMateIds: this.sessionMateIds,
    });
    this.patchState({
      canSubmit: this.draftService.createCurrentMateDefinition(this.state, this.mateDocument) !== null,
    });
    return result.affectedMateIds;
  }

  /**
   * 编辑已经创建的配合。
   *
   * 设计说明：
   * 该入口供后续 PropertyManager 编辑态和设计树右键编辑复用，调用方只能提供纯数据 updater，
   * 不能直接修改 Mate 文档或运行时对象；控制器统一负责命令执行、历史通知和当前预览状态刷新。
   *
   * @param mateId 要编辑的 Mate ID。
   * @param updater Mate Definition 更新函数。
   */
  public editMate(
    mateId: string,
    updater: (mate: AssemblyMateDefinition) => AssemblyMateDefinition,
  ): Promise<void> {
    this.restorePreview();
    const beforeDocument = this.createDocumentWithCurrentComponentStates(this.mateDocument);
    const nextDocument = this.createDocumentWithCurrentComponentStates(executeAssemblyMateCommand(
      beforeDocument,
      {
        type: 'assemblyMate.edit',
        mateId,
        updater,
      },
    ).document);
    return this.commitCommandDocumentChange({
      beforeDocument,
      nextDocument,
      previousSessionMateIds: this.sessionMateIds,
      nextSessionMateIds: this.sessionMateIds,
    }).then(() => {
      this.patchState({
        canSubmit: this.draftService.createCurrentMateDefinition(this.state, this.mateDocument) !== null,
      });
    });
  }

  /**
   * 抑制或解除抑制已经创建的配合。
   *
   * @param mateId 要抑制或解除抑制的 Mate ID。
   * @param suppressed 是否抑制。
   */
  public async suppressMate(mateId: string, suppressed: boolean): Promise<void> {
    this.restorePreview();
    const beforeDocument = this.createDocumentWithCurrentComponentStates(this.mateDocument);
    const nextDocument = this.createDocumentWithCurrentComponentStates(executeAssemblyMateCommand(
      beforeDocument,
      {
        type: 'assemblyMate.suppress',
        mateId,
        suppressed,
      },
    ).document);
    await this.commitCommandDocumentChange({
      beforeDocument,
      nextDocument,
      previousSessionMateIds: this.sessionMateIds,
      nextSessionMateIds: this.sessionMateIds,
    });
    this.patchState({
      canSubmit: this.draftService.createCurrentMateDefinition(this.state, this.mateDocument) !== null,
    });
  }

  /**
   * 选中或清除 Mate 列表中的已提交配合。
   *
   * 设计说明：
   * 1. SOLIDWORKS 在 PropertyManager 的 `Mates` 列表中选中配合时会高亮该配合的参与几何；
   * 2. web-client 只传入 mateId，拓扑引用恢复、对象解析和高亮事件构造都留在 cad-core；
   * 3. 该高亮只影响命令态展示，不写入 Mate Definition，也不依赖运行时 Object3D.uuid 持久化。
   *
   * @param mateId Mate ID；为 null 时恢复当前候选高亮。
   */
  public selectMate(mateId: string | null): void {
    if (!mateId) {
      this.runtime.syncCandidateHighlights(this.state.candidates, { force: true });
      return;
    }

    const mate = this.mateDocument.mates.find((item) => item.mateId === mateId);
    if (!mate) {
      this.runtime.syncCandidateHighlights(this.state.candidates, { force: true });
      return;
    }

    const payloads = this.runtime.createMateHighlightPayloads(mate);
    if (payloads.length === 0) {
      this.runtime.syncCandidateHighlights(this.state.candidates, { force: true });
      return;
    }
    this.viewer.setImportedModelCommandSelectedEntities(payloads, { force: true });
  }

  /**
   * 批量选中或清除 Mate 列表中的已提交配合。
   *
   * @param mateIds Mate ID 列表；为空时恢复当前候选高亮。
   */
  public selectMates(mateIds: readonly string[]): void {
    if (mateIds.length === 0) {
      this.runtime.syncCandidateHighlights(this.state.candidates, { force: true });
      return;
    }

    const mateIdSet = new Set(mateIds);
    const payloadByObjectId = new Map<string, EntitySelectedEventPayload>();
    this.mateDocument.mates
      .filter((mate) => mateIdSet.has(mate.mateId))
      .flatMap((mate) => this.runtime.createMateHighlightPayloads(mate))
      .forEach((payload) => {
        /**
         * 多条 Mate 可能引用同一个实体。按 objectId 去重可避免 imported-model 高亮层
         * 重复处理同一对象，同时保留首次出现顺序，便于 UI 视觉稳定。
         */
        if (!payloadByObjectId.has(payload.objectId)) {
          payloadByObjectId.set(payload.objectId, payload);
        }
      });

    const payloads = [...payloadByObjectId.values()];
    if (payloads.length === 0) {
      this.runtime.syncCandidateHighlights(this.state.candidates, { force: true });
      return;
    }
    this.viewer.setImportedModelCommandSelectedEntities(payloads, { force: true });
  }

  /**
   * 删除 Mate PropertyManager 中指定槽位的待配合实体。
   *
   * 设计说明：
   * 1. SOLIDWORKS 允许在 `Entities to Mate` 列表中删除单个实体后继续从图形区补选；
   * 2. 删除候选会改变合法配合类型，必须在 cad-core 内重新计算兼容性、默认类型与预览；
   * 3. web-client 只转发列表索引，不能自行维护 MateEntityRef 或几何判断。
   *
   * @param index 要删除的候选槽位索引。
   */
  public removeCandidateAt(index: number): void {
    this.applyCandidateSelectionMutation(removeAssemblyMateSelectionCandidateAt(this.state.candidates, index));
  }

  /**
   * 删除 Mate PropertyManager 中多个已选实体槽位。
   *
   * @param indices 要删除的候选槽位索引集合。
   */
  public removeCandidatesAt(indices: readonly number[]): void {
    this.applyCandidateSelectionMutation(removeAssemblyMateSelectionCandidatesAt(this.state.candidates, indices));
  }

  /**
   * 应用候选列表变更并重算 Mate 命令态。
   *
   * @param candidates 已由 selection 纯函数处理后的候选列表。
   */
  private applyCandidateSelectionMutation(candidates: readonly AssemblyMateSelectionCandidate[]): void {
    this.runtime.clearOrdinarySelectionForCandidateEdit(candidates);
    const pair = resolveMateCandidatePair(candidates);
    const compatibility = pair
      ? resolveAssemblyMateCompatibility(pair[0].entityRef, pair[1].entityRef)
      : null;
    const nextState = {
      candidates,
      candidateReplacementIndex: null,
      angleReferenceEntity: null,
      selectionTarget: 'mateEntities',
      compatibility,
      selectedMateType: this.state.selectedMateType,
      solveStatus: MateSolveStatus.NotRun,
      errorMessage: null,
      canSubmit: false,
    } satisfies Partial<AssemblyMateControllerState>;

    const selectedMateType = resolveDefaultAssemblyMateType({
      ...this.state,
      ...nextState,
    } as AssemblyMateControllerState);
    const lockRotationEnabled = resolveAssemblyMateLockRotationEnabled(
      selectedMateType,
      candidates,
      { mateDefinitions: this.mateDocument.mates },
    );
    const alignmentEnabled = resolveAssemblyMateAlignmentEnabled(selectedMateType, candidates);
    const stateBeforeDimensionPatch = {
      ...nextState,
      selectedMateType,
      alignmentEnabled,
      alignmentBranchAvailability: alignmentEnabled
        ? ENABLED_ALIGNMENT_BRANCH_AVAILABILITY
        : DISABLED_ALIGNMENT_BRANCH_AVAILABILITY,
      lockRotationEnabled,
      lockRotation: lockRotationEnabled ? this.state.lockRotation : false,
    } satisfies Partial<AssemblyMateControllerState>;
    const provisionalState = {
      ...this.state,
      ...stateBeforeDimensionPatch,
    } as AssemblyMateControllerState;
    this.patchState({
      ...stateBeforeDimensionPatch,
      ...resolveAssemblyMateConcentricMisalignmentStatePatch({
        mateType: selectedMateType,
        candidates,
        mateDefinitions: this.mateDocument.mates,
        state: provisionalState,
      }),
      ...this.draftService.resolveMeasuredDistancePatch(selectedMateType, candidates, this.state, {
        preferredDistanceMode: this.preferredRoundDistanceMode,
        preferredDistanceEntityModes: this.preferredRoundDistanceEntityModes,
      }),
    });
    this.runtime.pruneCandidateHighlightPayloads(candidates);
    this.runtime.syncCandidateHighlights(candidates);
    this.refreshPreview();
  }

  /**
   * 在组件移动或旋转后重求解已提交配合与当前可解草稿配合。
   *
   * 设计说明：
   * 1. 该入口服务于“移动零部件”实时预览，业务含义属于 cad-core，不放到 web-client；
   * 2. 被用户直接拖动的组件只作为交互驱动侧，真实 fixed 组件仍拥有更高优先级；
   * 3. 只求解与锚点连通的 Mate 图，避免 pointermove 高频路径全量扫描场景；
   * 4. Mate 预览阶段的草稿 Mate 必须作为临时约束参与求解，才能对齐 SOLIDWORKS 的拖动自由度限制。
   *
   * @param input 重求解输入。
   * @returns 重求解结果；无相关 Mate 时返回 `null`。
   */
  public solveCommittedMatesAfterComponentTransform(input: {
    readonly anchorComponentInstanceId: string;
    readonly drivenComponentConstraints?: StandardMateSolverInput['drivenComponentConstraints'];
    readonly updateDocumentStatus?: boolean;
    /** 求解失败自动采集出口标识；探测性求解传 `null` 跳过采集。 */
    readonly unsolvedCaptureContext?: string | null;
  }): AssemblyMateCommittedSolveResult | null {
    return this.solveService.solveAfterComponentTransform({
      ...input,
      transientMateDefinition: this.resolveActiveTransientMateDefinitionForDrag(),
    });
  }

  /**
   * 在 Mate 文档恢复完成后立即求解已提交配合。
   *
   * 设计说明：
   * 1. 文档恢复会把保存的组件快照写回场景，但子装配版本切换后的新几何还需要一次约束收敛；
   * 2. 该入口属于 cad-core 装配语义，不要求 web-client 伪造移动命令或拼接 solver 输入；
   * 3. 仅对当前文档中可参与求解的已提交 Mate 生效，引用缺失的 Mate 继续保留诊断状态。
   *
   * @param input 恢复后求解选项。
   * @returns 求解结果；没有可用 Mate 时返回 `null`。
   */
  public solveCommittedMatesAfterDocumentRestore(
    input: PuppyCAD.AssemblyMateDocumentRestoreSolveOptions = {},
  ): AssemblyMateCommittedSolveResult | null {
    return this.solveService.solveAfterDocumentRestore(input);
  }

  /**
   * 解析组件当前可用于“旋转零部件”命令的 Mate 剩余旋转轴。
   *
   * 设计说明：
   * 旋转命令属于 cad-core 装配交互能力，不能在 UI 或 Viewer 层根据选中状态临时拼装自由度。
   * 控制器门面只暴露 plain-data 轴线，真正的 Concentric / Coincident 图语义仍由底层求解服务负责。
   *
   * @param input 组件旋转自由度解析输入。
   * @returns 可用旋转轴；当前组件无明确剩余旋转自由度时返回 null。
   */
  public resolveComponentRotationAxisForTransform(input: {
    readonly componentInstanceId: string;
  }): AssemblyMateComponentRotationAxis | null {
    return this.solveService.resolveComponentRotationAxis({
      ...input,
      transientMateDefinition: this.resolveActiveTransientMateDefinitionForDrag(),
    });
  }

  /**
   * 解析组件变换时需要整体写入 transform 的全浮动刚性组合对象。
   *
   * 设计说明：
   * solver 只返回组件 ID，控制器负责把组件 ID 映射到当前场景的真实 transform root。这样上层拖拽和数值旋转
   * 只消费 Object3D 集合，不需要理解 Mate 图结构。
   *
   * @param input 组件刚性联动解析输入。
   * @returns 需要共同写入 transform 的场景对象；没有刚性组时返回空数组。
   */
  public resolveFloatingRigidLinkedTransformObjectsForTransform(input: {
    readonly componentInstanceId: string;
  }): readonly Object3D[] {
    const rigidGroup = this.solveService.resolveFloatingRigidTransformGroup({
      ...input,
      transientMateDefinition: this.resolveActiveTransientMateDefinitionForDrag(),
    });
    if (!rigidGroup) {
      return [];
    }

    return rigidGroup.componentInstanceIds
      .map((componentInstanceId) => this.runtime.resolveComponentObject(componentInstanceId))
      .filter((object): object is Object3D => Boolean(object));
  }

  /**
   * 解析组件所在的整个连通装配的场景对象，用于“整体绕鼠标方向刚性旋转”。
   *
   * 设计说明：
   * 当选中零件的剩余自由度不支持鼠标方向旋转时，Rotate 需要把它所在的整个已提交配合连通装配当作单一自由
   * 刚体整体旋转。solver 只判定连通装配是否全浮动并返回组件 ID，控制器负责映射到当前场景 transform root，
   * 让上层旋转入口只消费 Object3D 集合。含固定件（整组被锚住）时返回空数组，由上层回退剩余轴约束旋转。
   *
   * @param input 组件连通装配解析输入。
   * @returns 需要共同刚性旋转的场景对象；无法整体自由旋转时返回空数组。
   */
  public resolveFloatingConnectedLinkedTransformObjectsForTransform(input: {
    readonly componentInstanceId: string;
  }): readonly Object3D[] {
    const connectedGroup = this.solveService.resolveFloatingConnectedTransformGroup(input);
    if (!connectedGroup) {
      return [];
    }

    return connectedGroup.componentInstanceIds
      .map((componentInstanceId) => this.runtime.resolveComponentObject(componentInstanceId))
      .filter((object): object is Object3D => Boolean(object));
  }

  /**
   * 解析当前拖动重求解需要附带的临时 Mate。
   *
   * @returns 当前可解且正在显示预览的草稿 Mate；不存在时返回 `null`。
   */
  private resolveActiveTransientMateDefinitionForDrag(): AssemblyMateDefinition | null {
    if (!this.state.active || !this.state.showPreview || !this.previewResult) {
      return null;
    }
    const definition = this.draftService.createCurrentMateDefinition(this.state, this.mateDocument);
    if (!definition) {
      return null;
    }

    const previewState = resolveAssemblyMatePropertyManagerPreviewState({
      solveStatus: this.previewResult.status,
      diagnostics: this.previewResult.diagnostics,
      transientMateId: definition.mateId,
    });
    return canSubmitAssemblyMateSolveStatus(previewState.solveStatus) ? definition : null;
  }

  /**
   * 判断指定对齐分支当前是否可选。
   *
   * @param alignment 对齐分支。
   * @param availability 分支可用性；缺省使用当前控制器状态。
   * @returns 是否可选。
   */
  private isAlignmentBranchAvailable(
    alignment: MateAlignment,
    availability: AssemblyMateAlignmentBranchAvailability = this.state.alignmentBranchAvailability,
  ): boolean {
    return alignment === MateAlignment.Aligned
      ? availability.aligned
      : availability.antiAligned;
  }

  /**
   * 判断两个对齐分支可用性是否一致。
   *
   * @param first 第一组可用性。
   * @param second 第二组可用性。
   * @returns 是否一致。
   */
  private areAlignmentBranchAvailabilitiesEqual(
    first: AssemblyMateAlignmentBranchAvailability,
    second: AssemblyMateAlignmentBranchAvailability,
  ): boolean {
    return first.aligned === second.aligned && first.antiAligned === second.antiAligned;
  }

  /**
   * 用指定 solver input 做一次纯预览求解评估。
   *
   * 设计说明：
   * 该函数只运行纯求解，不写入 `previewResult`，也不把 transform 应用到场景。
   * 因此可以安全地用于 PropertyManager 默认对齐分支探测，避免 Vue 响应式链路接触 three.js 对象。
   *
   * @param solverInput 预览求解输入。
   * @param transientMateId 当前草稿 Mate ID。
   * @returns 预览评估结果。
   */
  private evaluatePreviewSolverInput(
    solverInput: StandardMateSolverInput,
    transientMateId: string,
  ): AssemblyMatePreviewEvaluation {
    const result = createAssemblyMatePreview(solverInput);
    const previewState = resolveAssemblyMatePropertyManagerPreviewState({
      solveStatus: result.status,
      diagnostics: result.diagnostics,
      transientMateId,
    });
    return {
      result,
      previewState,
    };
  }

  /**
   * 用指定 Mate Definition 做一次纯预览求解评估。
   *
   * @param definition 草稿 Mate 定义。
   * @returns 预览评估结果；缺少组件上下文时返回 `null`。
   */
  private evaluatePreviewDefinition(
    definition: AssemblyMateDefinition,
    options: AssemblyMatePreviewEvaluationOptions = {},
  ): AssemblyMatePreviewEvaluation | null {
    const solverInput = this.solveService.createPreviewSolverInput(definition, {
      useTransientInitialPlacement: options.useTransientInitialPlacement,
    });
    return solverInput ? this.evaluatePreviewSolverInput(solverInput, definition.mateId) : null;
  }

  /**
   * 替换预览 solver input 中的当前草稿 Mate。
   *
   * @param solverInput 原始 solver input。
   * @param definition 替换后的草稿 Mate。
   * @returns 替换后的 solver input。
   */
  private replacePreviewSolverInputDefinition(
    solverInput: StandardMateSolverInput,
    definition: AssemblyMateDefinition,
  ): StandardMateSolverInput {
    return {
      ...solverInput,
      mateDefinitions: solverInput.mateDefinitions.map((mate) => (
        mate.mateId === definition.mateId ? definition : mate
      )),
    };
  }

  /**
   * 判断指定对齐分支下的草稿 Mate 是否可提交。
   *
   * @param definition 草稿 Mate 定义。
   * @param alignment 对齐分支。
   * @param solverInput 已构造好的预览 solver input。
   * @returns 当前分支是否可提交。
   */
  private canSubmitPreviewAlignmentBranch(
    definition: AssemblyMateDefinition,
    alignment: MateAlignment,
    solverInput: StandardMateSolverInput,
  ): boolean {
    const alignedDefinition = {
      ...definition,
      alignment,
    };
    const evaluation = this.evaluatePreviewSolverInput(
      this.replacePreviewSolverInputDefinition(solverInput, alignedDefinition),
      definition.mateId,
    );
    return canSubmitAssemblyMateSolveStatus(evaluation.previewState.solveStatus);
  }

  /**
   * 解析当前草稿 Mate 的可用对齐分支与默认选择。
   *
   * 设计说明：
   * SOLIDWORKS 在新增 Mate 时会优先选择能在现有约束剩余自由度内求解的对齐分支。
   * 这里不修改任何已提交 Mate，只把不可解的分支从当前 PropertyManager 中禁用。
   *
   * @param definition 草稿 Mate 定义。
   * @param solverInput 已构造好的预览 solver input。
   * @returns 对齐分支解析结果。
   */
  private resolveAlignmentBranchResolution(
    definition: AssemblyMateDefinition,
    solverInput: StandardMateSolverInput,
  ): AssemblyMateAlignmentBranchResolution {
    if (!this.state.alignmentEnabled) {
      return {
        availability: DISABLED_ALIGNMENT_BRANCH_AVAILABILITY,
        alignment: this.state.alignment,
      };
    }

    const availability: AssemblyMateAlignmentBranchAvailability = {
      aligned: this.canSubmitPreviewAlignmentBranch(definition, MateAlignment.Aligned, solverInput),
      antiAligned: this.canSubmitPreviewAlignmentBranch(definition, MateAlignment.AntiAligned, solverInput),
    };
    const alignment = this.isAlignmentBranchAvailable(this.state.alignment, availability)
      ? this.state.alignment
      : availability.aligned
        ? MateAlignment.Aligned
        : availability.antiAligned
          ? MateAlignment.AntiAligned
          : this.state.alignment;

    return {
      availability,
      alignment,
    };
  }

  /**
   * 将对齐分支解析结果写回状态并返回最终草稿 Mate。
   *
   * @param definition 原始草稿 Mate 定义。
   * @param solverInput 已构造好的预览 solver input。
   * @returns 已按可解分支修正的草稿 Mate 和 solver input。
   */
  private applyAlignmentBranchResolution(
    definition: AssemblyMateDefinition,
    solverInput: StandardMateSolverInput,
  ): AssemblyMateResolvedPreviewInput {
    const resolution = this.resolveAlignmentBranchResolution(definition, solverInput);
    const alignmentChanged = resolution.alignment !== this.state.alignment;
    const availabilityChanged = !this.areAlignmentBranchAvailabilitiesEqual(
      resolution.availability,
      this.state.alignmentBranchAvailability,
    );
    if (alignmentChanged || availabilityChanged) {
      this.patchState({
        ...(alignmentChanged ? { alignment: resolution.alignment } : {}),
        ...(availabilityChanged ? { alignmentBranchAvailability: resolution.availability } : {}),
      });
    }

    const resolvedDefinition = alignmentChanged
      ? {
          ...definition,
          alignment: resolution.alignment,
        }
      : definition;
    return {
      definition: resolvedDefinition,
      solverInput: alignmentChanged
        ? this.replacePreviewSolverInputDefinition(solverInput, resolvedDefinition)
        : solverInput,
    };
  }

  /**
   * 取消当前配合。
   */
  public cancel(): void {
    this.restorePreview();
    this.sessionMateIds = [];
    this.runtime.clearCandidateHighlightPayloads();
    this.restoreMateSelectionMode();
    this.patchState({
      active: false,
      ...createEmptyAssemblyMateControllerSessionStatePatch(),
    });
  }

  /**
   * 销毁控制器。
   */
  public dispose(): void {
    this.cancel();
    this.runtime.dispose();
    this.selectionSubscription?.unsubscribe();
    this.selectionSubscription = null;
    this.stateListeners.clear();
  }

  /**
   * 进入 Mate 命令专属拓扑选择模式。
   *
   * 设计说明：
   * SOLIDWORKS Mate PropertyManager 的 `Entities to Mate` 直接吸收面、线/边、点等拓扑实体。
   * 如果 Viewer 仍停留在普通 object 选择模式，点击图形区只会选中零部件节点，
   * `SelectionUpdated` 不会携带 `AssemblyTopologySelectionRef`，最终导致配合选择为空。
   * 因此选择模式切换必须由 cad-core 命令生命周期托管，而不是交给 web-client 猜测。
   */
  private enterMateSelectionMode(): void {
    if (this.previousSelectionMode === null) {
      this.previousSelectionMode = this.viewer.getSelectionMode?.() ?? 'object';
    }
    this.viewer.setSelectionMode?.(ASSEMBLY_MATE_SELECTION_MODE, { preserveSelection: false });
  }

  /**
   * 恢复进入 Mate 命令前的选择模式。
   *
   * 设计说明：
   * 新建 Mate 提交后 SOLIDWORKS 会保持 PropertyManager 打开，所以不能在每次提交后恢复；
   * 只有取消、编辑提交退出或销毁控制器时，才恢复普通装配体对象选择。
   */
  private restoreMateSelectionMode(): void {
    const selectionMode = this.previousSelectionMode;
    if (selectionMode === null) {
      return;
    }
    this.previousSelectionMode = null;
    this.viewer.setSelectionMode?.(selectionMode, { preserveSelection: false });
  }

  /**
   * 恢复 Mate 会话内的默认装配移动授权。
   *
   * 说明：
   * 1. SOLIDWORKS 在 Mate PropertyManager 打开期间允许用户拖动自由度未锁死的零部件；
   * 2. 这里只启用默认拖拽能力，不进入显式“移动零部件”命令态；
   * 3. 碰撞检测、物理动态等高级移动选项仍由 MoveComponentController 的显式命令路径管理。
   */
  private activateMateSessionMoveCapability(): void {
    this.viewer.moveComponentController?.activateDefaultMoveCapability?.();
  }

  /**
   * 处理选择更新。
   *
   * @param payload 选择事件快照。
   */
  private handleSelectionUpdated(payload: SelectionUpdatedEventPayload): void {
    if (!this.state.active) {
      return;
    }
    if (payload.selectionIntent === 'pointer-down' || payload.selectionIntent === 'drag-start') {
      /**
       * SOLIDWORKS 中 Mate PropertyManager 打开后仍可拖动零部件；拖拽起点需要普通选择高亮，
       * 但不能被 Mate 选择槽吸收。只有后续真正 click 结算的 SelectionUpdated 才创建配合候选。
       */
      return;
    }

    const incomingCandidates = createAssemblyMateSelectionCandidatesFromSnapshot(payload)
      .map((candidate) => replaceAssemblyMateCandidateEntityRef(
        candidate,
        this.draftService.withSelectionComponentTransform(candidate),
      ));
    if (incomingCandidates.length === 0) {
      return;
    }
    this.runtime.cacheCandidateHighlightPayloads(payload, incomingCandidates);

    if (this.state.selectionTarget === 'angleReference') {
      const [referenceCandidate] = incomingCandidates;
      if (!referenceCandidate) {
        return;
      }
      const pair = resolveMateCandidatePair(this.state.candidates);
      if (
        !pair
        || !canUseAssemblyMateAngleReferenceEntity(
          pair[0].entityRef,
          pair[1].entityRef,
          referenceCandidate.entityRef,
        )
      ) {
        this.notifySelectionWarning('角度配合参考实体必须垂直于两个角度配合实体。');
        this.runtime.clearOrdinarySelectionForCandidateEdit(this.state.candidates, { defer: true });
        this.runtime.pruneCandidateHighlightPayloads(this.state.candidates);
        this.runtime.syncCandidateHighlights(this.state.candidates);
        return;
      }
      this.patchState({
        angleReferenceEntity: referenceCandidate.entityRef,
        candidateReplacementIndex: null,
        selectionTarget: 'mateEntities',
        solveStatus: MateSolveStatus.NotRun,
        errorMessage: null,
        canSubmit: false,
      });
      this.runtime.clearOrdinarySelectionForCandidateEdit(this.state.candidates, { defer: true });
      this.runtime.pruneCandidateHighlightPayloads(this.state.candidates);
      this.runtime.syncCandidateHighlights(this.state.candidates);
      this.refreshPreview();
      return;
    }

    const candidates = this.state.candidateReplacementIndex === null
      ? absorbAssemblyMateSelectionCandidates(this.state.candidates, incomingCandidates, 2)
      : replaceAssemblyMateSelectionCandidateAt(
        this.state.candidates,
        incomingCandidates,
        this.state.candidateReplacementIndex,
        2,
      );
    const pair = resolveMateCandidatePair(candidates);
    const compatibility = pair
      ? resolveAssemblyMateCompatibility(pair[0].entityRef, pair[1].entityRef)
      : null;
    const nextState = {
      candidates,
      candidateReplacementIndex: null,
      angleReferenceEntity: null,
      selectionTarget: 'mateEntities',
      compatibility,
      selectedMateType: this.state.selectedMateType,
      solveStatus: MateSolveStatus.NotRun,
      errorMessage: null,
      canSubmit: false,
    } satisfies Partial<AssemblyMateControllerState>;

    const selectedMateType = resolveDefaultAssemblyMateType({
      ...this.state,
      ...nextState,
    } as AssemblyMateControllerState);
    const lockRotationEnabled = resolveAssemblyMateLockRotationEnabled(
      selectedMateType,
      candidates,
      { mateDefinitions: this.mateDocument.mates },
    );
    const alignmentEnabled = resolveAssemblyMateAlignmentEnabled(selectedMateType, candidates);
    const stateBeforeDimensionPatch = {
      ...nextState,
      selectedMateType,
      alignmentEnabled,
      alignmentBranchAvailability: alignmentEnabled
        ? ENABLED_ALIGNMENT_BRANCH_AVAILABILITY
        : DISABLED_ALIGNMENT_BRANCH_AVAILABILITY,
      lockRotationEnabled,
      lockRotation: lockRotationEnabled ? this.state.lockRotation : false,
    } satisfies Partial<AssemblyMateControllerState>;
    const provisionalState = {
      ...this.state,
      ...stateBeforeDimensionPatch,
    } as AssemblyMateControllerState;
    this.patchState({
      ...stateBeforeDimensionPatch,
      ...resolveAssemblyMateConcentricMisalignmentStatePatch({
        mateType: selectedMateType,
        candidates,
        mateDefinitions: this.mateDocument.mates,
        state: provisionalState,
      }),
      ...this.draftService.resolveMeasuredDistancePatch(selectedMateType, candidates, this.state, {
        preferredDistanceMode: this.preferredRoundDistanceMode,
        preferredDistanceEntityModes: this.preferredRoundDistanceEntityModes,
      }),
    });
    this.runtime.clearOrdinarySelectionForCandidateEdit(candidates, { defer: true });
    this.runtime.pruneCandidateHighlightPayloads(candidates);
    this.runtime.syncCandidateHighlights(candidates);
    this.refreshPreview();
  }

  /**
   * 刷新预览。
   */
  private refreshPreview(
    forcePreview = false,
    options: AssemblyMateRefreshPreviewOptions = {},
  ): void {
    const shouldPreserveCurrentPreviewPlacement = Boolean(
      options.preserveCurrentPreviewPlacement && this.runtime.hasPreviewSnapshot(),
    );
    const restoredPreview = shouldPreserveCurrentPreviewPlacement
      ? false
      : this.restorePreview({
          syncHighlights: false,
          requestRender: false,
        });
    if (!this.state.active) {
      if (restoredPreview) {
        this.viewer.requestRender();
      }
      return;
    }
    this.runtime.syncCandidateHighlights(this.state.candidates, { force: true });

    const definition = this.draftService.createCurrentMateDefinition(this.state, this.mateDocument);
    if (!definition) {
      this.previewResult = null;
      this.patchState({
        solveStatus: MateSolveStatus.NotRun,
        errorMessage: null,
        canSubmit: false,
        alignmentBranchAvailability: this.state.alignmentEnabled
          ? ENABLED_ALIGNMENT_BRANCH_AVAILABILITY
          : DISABLED_ALIGNMENT_BRANCH_AVAILABILITY,
      });
      this.runtime.syncCandidateHighlights(this.state.candidates, { force: true });
      if (restoredPreview) {
        this.viewer.requestRender();
      }
      return;
    }
    const solverInput = this.solveService.createPreviewSolverInput(definition, {
      useTransientInitialPlacement: options.useTransientInitialPlacement,
    });
    if (!solverInput) {
      this.previewResult = null;
      this.patchState({
        solveStatus: MateSolveStatus.MissingReference,
        errorMessage: '无法解析配合实体所属组件。',
        canSubmit: false,
      });
      this.runtime.syncCandidateHighlights(this.state.candidates, { force: true });
      if (restoredPreview) {
        this.viewer.requestRender();
      }
      return;
    }
    const resolvedPreviewInput = this.applyAlignmentBranchResolution(definition, solverInput);
    const previewEvaluation = this.evaluatePreviewSolverInput(
      resolvedPreviewInput.solverInput,
      resolvedPreviewInput.definition.mateId,
    );
    this.previewResult = previewEvaluation.result;
    if (previewEvaluation.result.status !== MateSolveStatus.Solved) {
      /**
       * 只在对齐分支解析后的**最终**预览评估处采集：分支探测（canSubmitPreviewAlignmentBranch）
       * 的失败是预期行为（PropertyManager 会禁用不可解分支），不属于需要采集的失败现场。
       */
      captureStandardMateSolverUnsolvedResult({
        context: 'mate-preview',
        solverInput: resolvedPreviewInput.solverInput,
        status: previewEvaluation.result.status,
        diagnostics: previewEvaluation.result.diagnostics,
      });
    }

    if (previewEvaluation.previewState.solveStatus === MateSolveStatus.Solved && (this.state.showPreview || forcePreview)) {
      /**
       * Show Preview 只决定是否把求解结果写入运行时场景；Mate 合法性诊断必须始终运行。
       * 这样关闭预览不会让冲突 Mate 在确认前短暂表现为可提交，同时也不会移动模型。
       */
      this.runtime.applyPreviewTransforms(previewEvaluation.result, this.state.candidates);
    }

    this.patchState({
      solveStatus: previewEvaluation.previewState.solveStatus,
      errorMessage: previewEvaluation.previewState.diagnostic?.message ?? null,
      canSubmit: canSubmitAssemblyMateSolveStatus(previewEvaluation.previewState.solveStatus),
    });
    this.runtime.syncCandidateHighlights(this.state.candidates, { force: true });
    if (previewEvaluation.previewState.solveStatus !== MateSolveStatus.Solved && restoredPreview) {
      this.viewer.requestRender();
    }
  }

  /**
   * 基于当前场景组件位姿评估草稿 Mate 预览。
   *
   * @param definition 草稿 Mate 定义。
   * @returns 预览评估结果；缺少组件上下文时返回 `null`。
   */
  private resolvePreviewEvaluation(
    definition: AssemblyMateDefinition,
    options: AssemblyMatePreviewEvaluationOptions = {},
  ): AssemblyMatePreviewEvaluation | null {
    const evaluation = this.evaluatePreviewDefinition(definition, options);
    if (!evaluation) {
      this.previewResult = null;
      return null;
    }

    this.previewResult = evaluation.result;
    return evaluation;
  }

  /**
   * 恢复预览。
   *
   * @param options 预览恢复选项。
   * @returns 是否实际回滚过预览 transform。
   */
  private restorePreview(options: AssemblyMatePreviewRestoreOptions = {}): boolean {
    const restored = this.runtime.restorePreview(this.state.candidates, options);
    this.previewResult = null;
    return restored;
  }

  /**
   * 清理预览快照但不回滚对象。
   */
  private clearPreviewSnapshotOnly(): void {
    this.runtime.clearPreviewSnapshotOnly();
    this.previewResult = null;
  }

  /**
   * 解析当前 PropertyManager 会话内新增的 Mate Definition。
   *
   * @returns 当前会话新增的配合集合。
   */
  private resolveSessionMates(): readonly AssemblyMateDefinition[] {
    const mateById = new Map(this.mateDocument.mates.map((mate) => [mate.mateId, mate]));
    return this.sessionMateIds
      .map((mateId) => mateById.get(mateId) ?? null)
      .filter((mate): mate is AssemblyMateDefinition => mate !== null);
  }

  /**
   * 读取文档涉及的组件 ID。
   *
   * @param document Mate 文档。
   * @param extraComponentIds 额外需要纳入事务快照的组件 ID。
   * @returns 去重后的组件 ID 列表。
   */
  private resolveDocumentComponentIds(
    document: AssemblyMateDocumentModel,
    extraComponentIds: readonly string[] = [],
  ): readonly string[] {
    const componentIds = new Set<string>([
      ...Object.keys(document.components),
      ...extraComponentIds,
    ]);
    document.mates.forEach((mate) => {
      resolveAssemblyMateComponentInstanceIds(mate).forEach((componentInstanceId) => {
        componentIds.add(componentInstanceId);
      });
    });
    return [...componentIds];
  }

  /**
   * 用当前场景中的真实组件位姿补齐 Mate 文档组件快照。
   *
   * @param document Mate 文档。
   * @param extraComponentIds 额外组件 ID。
   * @param options 组件快照读取选项。
   * @returns 带当前组件位姿的 Mate 文档。
   */
  private createDocumentWithCurrentComponentStates(
    document: AssemblyMateDocumentModel,
    extraComponentIds: readonly string[] = [],
    options: AssemblyMateDocumentComponentStateSnapshotOptions = {},
  ): AssemblyMateDocumentModel {
    const components = { ...document.components };
    this.resolveDocumentComponentIds(document, extraComponentIds).forEach((componentInstanceId) => {
      const componentState = options.preferPreviewRestoreStates
        ? this.runtime.resolvePreviewRestoreComponentState(componentInstanceId)
          ?? this.runtime.resolveComponentState(componentInstanceId)
        : this.runtime.resolveComponentState(componentInstanceId);
      if (!componentState) {
        return;
      }
      components[componentState.componentInstanceId] = componentState;
    });
    return setAssemblyMateDocumentComponents(document, components);
  }

  /**
   * 提交一次 Mate 命令文档变更。
   *
   * @param input 命令事务输入。
   */
  private async commitCommandDocumentChange(input: {
    readonly beforeDocument: AssemblyMateDocumentModel;
    readonly nextDocument: AssemblyMateDocumentModel;
    readonly previousSessionMateIds: readonly string[];
    readonly nextSessionMateIds: readonly string[];
    readonly afterApply?: () => void | Promise<void>;
  }): Promise<void> {
    this.mateDocument = input.nextDocument;
    this.sessionMateIds = [...input.nextSessionMateIds];

    try {
      if (input.afterApply) {
        await input.afterApply();
      }
      await this.notifyMateDocumentChanged({
        source: 'command',
        recordHistory: true,
        beforeDocument: input.beforeDocument,
      });
    } catch (error) {
      this.mateDocument = input.beforeDocument;
      this.sessionMateIds = [...input.previousSessionMateIds];
      this.runtime.applyDocumentComponentStates(input.beforeDocument.components);
      this.patchState({
        canSubmit: this.draftService.createCurrentMateDefinition(this.state, this.mateDocument) !== null,
      });
      throw error;
    }
  }

  /**
   * 通知宿主 Mate 文档已变化。
   *
   * @param context 文档变化上下文。
   */
  private async notifyMateDocumentChanged(context: AssemblyMateDocumentChangeContext): Promise<void> {
    await this.options.onMateDocumentChanged?.(this.mateDocument, context);
  }

  /**
   * 后台通知宿主 Mate 文档变化。
   *
   * @param context 文档变化上下文。
   */
  private notifyMateDocumentChangedInBackground(context: AssemblyMateDocumentChangeContext): void {
    void this.notifyMateDocumentChanged(context).catch(() => {
      this.notifySelectionWarning('配合状态同步失败，请刷新设计树后重试。');
    });
  }

  /**
   * 通过 Viewer 宿主统一提示选择警告。
   *
   * 设计说明：
   * 1. SOLIDWORKS 中 Angle 参考实体选错时不会把当前 Mate 草稿变成不可确认错误；
   * 2. cad-core 只负责判断并发出警告，web-client 通过既有全局消息通道展示；
   * 3. 这里不新增 UI 依赖，也不把无效参考实体写入控制器状态。
   *
   * @param message 警告文案。
   */
  private notifySelectionWarning(message: string): void {
    this.viewer.drawingNoticeCallback?.(message, 'warning');
  }

  /**
   * 局部更新状态。
   *
   * @param partial 状态补丁。
   */
  private patchState(partial: Partial<AssemblyMateControllerState>): void {
    this.state = normalizeAssemblyMatePropertyManagerControllerState({
      ...this.state,
      ...partial,
    });
    this.runtime.syncFirstSelectionTransparency(
      this.state.candidates,
      this.state.active && this.state.firstSelectionTransparent,
    );
    this.options.onStateChanged?.({ ...this.state });
    const viewModel = this.getViewModel();
    this.stateListeners.forEach((listener) => {
      listener(viewModel);
    });
  }
}
